import { gql } from '@apollo/client'
import { BodyText, Caption, Chip, Col, ColNumber, Container, Heading, Row } from '@farol-ds/react'
import classNames from 'classnames'
import { useMemo, useState } from 'react'

import { PlanFrequency, PlanFrequencyLabel } from '../../../../constants'
import { FrequencyToggle } from '../../../frequency-toggle'
import { PaywallCarousel } from '../../../paywall-carousel'
import { OfferCard } from '../offer-card'
import {
  DynamicMultipleOffersSwitchFrequencyLockContent_OfferLockDataFragment,
  DynamicMultipleOffersSwitchFrequencyLockContent_ViewerFragment,
} from './__generated__/dynamic-multiple-offers-switch-frequency-lock-content.graphql'
import styles from './dynamic-multiple-offers-switch-frequency-lock-content.module.scss'

export interface DynamicMultipleOffersSwitchFrequencyLockContentProps {
  offerLockData: DynamicMultipleOffersSwitchFrequencyLockContent_OfferLockDataFragment
  viewer: DynamicMultipleOffersSwitchFrequencyLockContent_ViewerFragment
  isPaywall?: boolean
}

export const DynamicMultipleOffersSwitchFrequencyLockContent = ({
  offerLockData,
  viewer,
  isPaywall = false,
}: DynamicMultipleOffersSwitchFrequencyLockContentProps) => {
  const { header, body } = offerLockData || {}

  const { toggle, title, subtitle } = header || {}
  const { options, defaultOption, description } = toggle || {}
  const { text, benefit } = description || {}
  const hasToggle = !!options.length

  const { cards } = body || {}

  const [currentFrequency, setCurrentFrequency] = useState(defaultOption || options[0])

  const cardsForFrequency = useMemo(() => {
    if (!currentFrequency) return [...cards]

    if (currentFrequency === PlanFrequencyLabel.MONTHLY) {
      return cards.filter((card) => card.offer.frequency === PlanFrequency.MONTHLY)
    }

    return cards.filter((card) => card.offer.frequency === PlanFrequency.YEARLY)
  }, [cards, currentFrequency])

  const handleChangeFrequency = (frequency: string) => {
    setCurrentFrequency(frequency)
  }

  return (
    <Container
      type={isPaywall ? 'default' : 'fluid'}
      className={classNames(styles.container, {
        [styles.singlePaywall]: isPaywall && cardsForFrequency.length < 3,
      })}
    >
      {isPaywall && (
        <Row className={styles.paywallHeader}>
          <Col xs={12}>
            <Heading size="lg">{title}</Heading>
            <BodyText size="lg">{subtitle}</BodyText>
          </Col>
        </Row>
      )}

      {hasToggle && (
        <Row>
          <Col xs={12} className={styles.toggle}>
            <FrequencyToggle
              options={options}
              currentFrequency={currentFrequency}
              onChangeFrequency={handleChangeFrequency}
            />
            <Caption>
              {text}{' '}
              <Chip kind="secondary" size="sm">
                <strong>{benefit}</strong>
              </Chip>
            </Caption>
          </Col>
        </Row>
      )}

      {isPaywall && cardsForFrequency.length > 2 ? (
        <PaywallCarousel
          cards={cardsForFrequency}
          renderCard={(card) => <OfferCard offerCardData={card} viewer={viewer} />}
          mobileClassName={styles.mobilePaywallOffersContent}
        />
      ) : (
        <Row>
          {cardsForFrequency.map((card, index) => (
            <Col
              key={`${card.title}-${index}`}
              xs={12}
              md={(12 / cardsForFrequency.length) as ColNumber}
              className={styles.offerCard}
            >
              <OfferCard offerCardData={card} viewer={viewer} />
            </Col>
          ))}
        </Row>
      )}
    </Container>
  )
}

DynamicMultipleOffersSwitchFrequencyLockContent.fragments = {
  offerLockData: gql`
    fragment DynamicMultipleOffersSwitchFrequencyLockContent_offerLockData on OfferLockData {
      header {
        title
        subtitle
        toggle {
          options
          defaultOption
          description {
            text
            benefit
          }
        }
      }
      body {
        cards {
          ...OfferCard_offerCardData
        }
      }
    }
    ${OfferCard.fragments.offerCardData}
  `,
  viewer: gql`
    fragment DynamicMultipleOffersSwitchFrequencyLockContent_viewer on Profile {
      ...OfferCard_viewer
    }
    ${OfferCard.fragments.viewer}
  `,
}
